import {
  useState,
  useEffect,
  useMemo,
  type PropsWithChildren,
  useEffectEvent,
} from "react";

import {
  TravellersSelectionProvider,
  useTravellersSelectionContext,
} from "@/features/travellers/contexts/TravellersSelectionContext";
import type { Props as TravellersSelectionContextProviderProps } from "@/features/travellers/contexts/TravellersSelectionContext/TravellersSelectionContextProvider";
import { getTravellerTypeAtTravelDate } from "@/features/travellers/utils/ageValidation";

import { HotelTravellersSelectionContext } from "./HotelTravellersSelectionContext";

type Props = TravellersSelectionContextProviderProps & PropsWithChildren;

export const HotelTravellersSelectionProvider = ({
  initialTravellersSlots,
  travelDate,
  children,
}: Props) => {
  return (
    <TravellersSelectionProvider
      initialTravellersSlots={initialTravellersSlots}
      travelDate={travelDate}
    >
      <HotelTravellersSelectionProviderInner travelDate={travelDate}>
        {children}
      </HotelTravellersSelectionProviderInner>
    </TravellersSelectionProvider>
  );
};

const HotelTravellersSelectionProviderInner = ({
  travelDate,
  children,
}: Props) => {
  const [leadTravellerId, setLeadTravellerId] = useState<string | null>(null);
  const { selectedTravellers } = useTravellersSelectionContext();

  const setDefaultLeadTraveller = useEffectEvent(() => {
    const defaultLeadTravellerId =
      selectedTravellers.find(
        (traveller) =>
          getTravellerTypeAtTravelDate(traveller.birthDate, travelDate) ===
          "adult",
      )?.id || null;

    setLeadTravellerId(defaultLeadTravellerId);
  });

  useEffect(() => {
    const handleTravellerUpdated: WindowEventHandler<"travellerupdated"> = (
      event,
    ) => {
      const newTravellerType = getTravellerTypeAtTravelDate(
        event.detail.travellerNewData.birthDate,
        travelDate,
      );

      if (
        event.detail.travellerNewData.id === leadTravellerId &&
        newTravellerType !== "adult"
      ) {
        setDefaultLeadTraveller();
      }
    };

    const handleTravellerRemoved: WindowEventHandler<"travellerremoved"> = (
      event,
    ) => {
      if (event.detail.travellerId === leadTravellerId) {
        setDefaultLeadTraveller();
      }
    };

    window.addEventListener("travellerupdated", handleTravellerUpdated);
    window.addEventListener("travellerremoved", handleTravellerRemoved);
    window.addEventListener("travellerdeselected", handleTravellerRemoved);

    return () => {
      window.removeEventListener("travellerupdated", handleTravellerUpdated);
      window.removeEventListener("travellerremoved", handleTravellerRemoved);
      window.removeEventListener("travellerdeselected", handleTravellerRemoved);
    };
  }, [leadTravellerId, travelDate]);

  useEffect(() => {
    if (!leadTravellerId) {
      setDefaultLeadTraveller();
    }
  }, [leadTravellerId, selectedTravellers]);

  const contextValue = useMemo(
    () => ({
      leadTravellerId,
      setLeadTravellerId,
    }),
    [leadTravellerId],
  );

  return (
    <HotelTravellersSelectionContext value={contextValue}>
      {children}
    </HotelTravellersSelectionContext>
  );
};
